// Shared draft form for the non-app dealer location create + edit
// flows on the Plants → right-pane Dealers section. Extracted from
// the section file so the section itself stays under the 300-line
// soft cap.
import { useMemo, useState } from "react";
import { AddressAutocompleteInput } from "@/components/common/AddressAutocompleteInput";
import { inputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { AddressAutocompletePick } from "@/lib/addressAutocompleteClient";
import { buildGoogleMapsEmbedUrl, readBrowserMapsEmbedKey } from "@/lib/maps";
import type { LocationDraft } from "./plantDealersDrafts";
import type { UseManufacturingPlantDealersTab } from "./useManufacturingPlantDealersTab";

type DraftSetter = (next: LocationDraft) => void;

function addressQuery(draft: LocationDraft): string {
  return [draft.street, draft.city, draft.state, draft.postal_code]
    .map((p) => p.trim())
    .filter(Boolean)
    .join(", ");
}

export function ManufacturingPlantDealersLocationForm({
  draft,
  setDraft,
  m,
}: {
  draft: LocationDraft;
  setDraft: DraftSetter;
  m: UseManufacturingPlantDealersTab;
}) {
  const [searchLine, setSearchLine] = useState(() => addressQuery(draft));

  const mapUrl = useMemo(() => {
    const q = addressQuery(draft);
    if (!q) return null;
    return buildGoogleMapsEmbedUrl({
      query: q,
      zoom: 14,
      apiKey: readBrowserMapsEmbedKey(),
    });
  }, [draft.street, draft.city, draft.state, draft.postal_code]);

  function applyPick(pick: AddressAutocompletePick) {
    const n = pick.normalized;
    setDraft({
      ...draft,
      street: n.street?.trim() || draft.street,
      city: n.city?.trim() || draft.city,
      state: n.state?.trim() || draft.state,
      postal_code: n.postal_code?.trim() || draft.postal_code,
    });
    setSearchLine(pick.formatted || addressQuery({
      ...draft,
      street: n.street?.trim() || draft.street,
      city: n.city?.trim() || draft.city,
      state: n.state?.trim() || draft.state,
      postal_code: n.postal_code?.trim() || draft.postal_code,
    }));
  }

  return (
    <div className="space-y-2">
      <label className="block text-xs text-gray-600 dark:text-zinc-300">
        Dealer location name
        <input
          className={`${inputClass} mt-1`}
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          placeholder="e.g. Mark's Sheds — Cherokee Lot"
        />
      </label>
      <label className="block text-xs text-gray-600 dark:text-zinc-300">
        Find address (Google)
        <div className="mt-1">
          <AddressAutocompleteInput
            value={searchLine}
            onChange={setSearchLine}
            onPick={applyPick}
            className={inputClass}
            placeholder="Start typing a street address…"
          />
        </div>
      </label>
      <div className="grid grid-cols-2 gap-2">
        <label className="block text-xs text-gray-600 dark:text-zinc-300 col-span-2">
          Street
          <input
            className={`${inputClass} mt-1`}
            value={draft.street}
            onChange={(e) => setDraft({ ...draft, street: e.target.value })}
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          City
          <input
            className={`${inputClass} mt-1`}
            value={draft.city}
            onChange={(e) => setDraft({ ...draft, city: e.target.value })}
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          State
          <input
            className={`${inputClass} mt-1`}
            value={draft.state}
            onChange={(e) => setDraft({ ...draft, state: e.target.value })}
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Postal code
          <input
            className={`${inputClass} mt-1`}
            value={draft.postal_code}
            onChange={(e) => setDraft({ ...draft, postal_code: e.target.value })}
          />
        </label>
      </div>
      {mapUrl ? (
        <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-zinc-700">
          <iframe
            title="Dealer location map"
            src={mapUrl}
            className="h-40 w-full border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      ) : (
        <p className="text-[11px] text-gray-500 dark:text-zinc-400">
          Enter or pick an address to preview it on the map.
        </p>
      )}
      <label className="block text-xs text-gray-600 dark:text-zinc-300">
        Primary plant
        <select
          className={`${inputClass} mt-1`}
          value={draft.primary_plant_id}
          onChange={(e) => setDraft({ ...draft, primary_plant_id: e.target.value })}
        >
          <option value="">(no primary plant)</option>
          {m.plants
            .filter((p) => p.is_active)
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.plant_code} · {p.name}
              </option>
            ))}
        </select>
      </label>
      <label className="block text-xs text-gray-600 dark:text-zinc-300">
        Linked dealer org (optional — for the "upgrade to app-using" path)
        <select
          className={`${inputClass} mt-1`}
          value={draft.linked_dealer_org_id}
          onChange={(e) => setDraft({ ...draft, linked_dealer_org_id: e.target.value })}
        >
          <option value="">(not linked)</option>
          {m.subscriberDealers.map((s) => (
            <option key={s.dealer_org_id} value={s.dealer_org_id}>
              {s.dealer_org_name}
            </option>
          ))}
        </select>
      </label>
      <div className="grid grid-cols-3 gap-2">
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Contact name
          <input
            className={`${inputClass} mt-1`}
            value={draft.contact_name}
            onChange={(e) => setDraft({ ...draft, contact_name: e.target.value })}
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Contact phone
          <input
            className={`${inputClass} mt-1`}
            value={draft.contact_phone}
            onChange={(e) => setDraft({ ...draft, contact_phone: e.target.value })}
          />
        </label>
        <label className="block text-xs text-gray-600 dark:text-zinc-300">
          Contact email
          <input
            className={`${inputClass} mt-1`}
            value={draft.contact_email}
            onChange={(e) => setDraft({ ...draft, contact_email: e.target.value })}
          />
        </label>
      </div>
      <label className="block text-xs text-gray-600 dark:text-zinc-300">
        Notes
        <input
          className={`${inputClass} mt-1`}
          value={draft.notes}
          onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
        />
      </label>
      <label className="flex items-center gap-2 text-xs text-gray-600 dark:text-zinc-300">
        <input
          type="checkbox"
          checked={draft.is_active}
          onChange={(e) => setDraft({ ...draft, is_active: e.target.checked })}
        />
        Active
      </label>
    </div>
  );
}
